import type * as React from 'react';

import {
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    INLINE_EMOJI,
    INLINE_IMAGE,
    INLINE_LINK,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import { Block, Document, Inline } from '@gitbook/slate';
import type { BlockProps, MarkProps, NodeProps } from '@gitbook/slate-react';

import {
    BasicBlockCode,
    BasicBlockCodeLine,
    BasicBlockHeading,
    BasicBlockImage,
    BasicBlockImages,
    BasicBlockList,
    BasicBlockListItem,
    BasicBlockParagraph,
    BasicInlineEmoji,
    BasicInlineImage,
    BasicInlineLink,
} from './components';
import {
    MarkBold,
    MarkCode,
    MarkItalic,
    MarkStrikethrough,
    MarkSubscript,
    MarkSuperscript,
} from './marks';

/**
 * Rendering method for basic nodes of the PlainMarkdown component
 */
export function renderNode(props: NodeProps) {
    if (Block.isBlock(props.node) && !Inline.isInline(props.parent)) {
        const blockProps = props as BlockProps;

        switch (props.node.type) {
            case BLOCK_PARAGRAPH:
                return (
                    <BasicBlockParagraph {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_HEADING_1:
            case BLOCK_HEADING_2:
            case BLOCK_HEADING_3:
                return (
                    <BasicBlockHeading {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_LIST_UL:
            case BLOCK_LIST_OL:
            case BLOCK_LIST_TASKS:
                return <BasicBlockList {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_LIST_ITEM:
                return (
                    <BasicBlockListItem {...blockProps} node={props.node} parent={props.parent} />
                );
            case BLOCK_IMAGES:
                return <BasicBlockImages {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_IMAGE:
                return <BasicBlockImage {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_CODE:
                return <BasicBlockCode {...blockProps} node={props.node} parent={props.parent} />;
            case BLOCK_CODE_LINE:
                return (
                    <BasicBlockCodeLine {...blockProps} node={props.node} parent={props.parent} />
                );
        }
    }

    if (Inline.isInline(props.node) && !Document.isDocument(props.parent)) {
        switch (props.node.type) {
            case INLINE_IMAGE:
                return <BasicInlineImage {...props} node={props.node} parent={props.parent} />;
            case INLINE_LINK:
                return <BasicInlineLink {...props} node={props.node} parent={props.parent} />;
            case INLINE_EMOJI:
                return <BasicInlineEmoji {...props} node={props.node} parent={props.parent} />;
        }
    }

    return null;
}

/**
 * Rendering method for marks.
 */
export function renderMark(props: MarkProps): React.ReactNode {
    switch (props.mark.type) {
        case MARK_BOLD:
            return <MarkBold {...props} />;
        case MARK_ITALIC:
            return <MarkItalic {...props} />;
        case MARK_STRIKETHROUGH:
            return <MarkStrikethrough {...props} />;
        case MARK_SUPERSCRIPT:
            return <MarkSuperscript {...props} />;
        case MARK_SUBSCRIPT:
            return <MarkSubscript {...props} />;
        case MARK_CODE:
            return <MarkCode {...props} />;
    }
    return null;
}
